import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRbac } from "@its-engineering-and-development/rbac-react";
import { ALL_ACTION_KEYS } from "@/auth/permission-actions";
import { refreshAccessToken } from "@/api/auth-client";
import { expenseAdminPermissionResolver, expenseAdminTokenSource, getPermissionsLastValidatedAt } from "@/auth";
import { useAppDispatch, useAppSelector } from "@/store";
import { selectAccessDenied, setCredentials } from "@/store/auth.slice";
import { selectAllowedActions } from "@/store/permissions.slice";
import { useLogout } from "@/hooks/use-logout";
import { ErrorPage } from "@/pages/error";
import { isTokenExpired } from "@/utils/jwt-utils";
import { LoadingState, LOADING_STATE_VARIANT } from "@/components/base/loading-state/loading-state";
import { AccessCountdownContext } from "./access-countdown-context";

/** How many times we auto-refresh the token before concluding the result is final. */
const MAX_AUTO_ATTEMPTS = 1;
const PERMISSIONS_REFRESH_INTERVAL_MS = 15 * 60 * 1000;
const TOKEN_REFRESH_BUFFER_SECONDS = 60;
/** Seconds the access-denied notice stays up before redirecting to /login. */
const REDIRECT_COUNTDOWN_SECONDS = 5;

const NO_ACCESS_MESSAGE = "You don't have permission to access Expense Admin. Contact your administrator if you believe this is a mistake.";
const NETWORK_ERROR_MESSAGE = "We couldn't load your permissions. Please check your connection and try again.";

/** True when a non-expired access token is present (an active authenticated session). */
function hasValidSession(): boolean {
    const token = expenseAdminTokenSource.getAccessToken();
    return !!token && !isTokenExpired(token);
}

/**
 * AccessGuard — enforces that an authenticated user actually has at least one
 * permission before rendering the app shell.
 *
 * Handles two edge cases the requirements call out:
 *  1. Token-not-refreshed race: the first `/permissions` call can come back
 *     empty (or 401) because the access token is stale. We refresh the token
 *     once and let RBAC re-resolve before deciding anything.
 *  2. No access: if, after that retry, the user still has zero permissions,
 *     their session is ended and they are redirected to /login with an English
 *     explanation. A genuine service failure instead shows a retryable error.
 */
export function AccessGuard({ children }: { children: ReactNode }) {
    const { status } = useRbac();
    const allowedActions = useAppSelector(selectAllowedActions);
    const accessDenied = useAppSelector(selectAccessDenied);
    const dispatch = useAppDispatch();
    const logout = useLogout();

    const hasAccess = allowedActions.some((action) => (ALL_ACTION_KEYS as readonly string[]).includes(action));

    const [attempts, setAttempts] = useState(0);
    const [isRetrying, setIsRetrying] = useState(false);
    const [countdown, setCountdown] = useState<number | null>(null);
    const attemptingRef = useRef(false);
    const loggingOutRef = useRef(false);
    // Latched true once the user has been authorized and the shell has rendered.
    const shellReadyRef = useRef(false);

    // Shared token refresh: swap the access token, persist it, and notify the
    // token source so RBAC re-resolves. Both the background timer and the
    // manual retry rely on this to stay consistent.
    const refreshTokenAndNotify = useCallback(async () => {
        const refreshed = await refreshAccessToken();
        dispatch(setCredentials({ accessToken: refreshed.accessToken }));
        expenseAdminTokenSource.notifyTokenChange();
        return refreshed.accessToken;
    }, [dispatch]);

    // End the session for good: a revoked account (refresh token rejected) or a
    // resolved-but-empty permission set both mean the user can no longer use the
    // app. Guarded so it runs at most once.
    const endSession = useCallback(() => {
        if (loggingOutRef.current) {
            return;
        }
        loggingOutRef.current = true;
        if (hasValidSession()) {
            // Authenticated but revoked: show the access-denied notice with a short
            // countdown so the user can read it before we redirect to /login.
            setCountdown(REDIRECT_COUNTDOWN_SECONDS);
            return;
        }
        // No valid session (leftover/expired token, or an already-cleared session):
        // the visitor simply isn't logged in — sign out silently to the login form.
        void logout(undefined);
    }, [logout]);

    // Drive the redirect countdown, then end the session once it reaches zero.
    useEffect(() => {
        if (countdown === null) {
            return;
        }
        if (countdown <= 0) {
            void logout({ message: NO_ACCESS_MESSAGE });
            return;
        }
        const timer = setTimeout(() => setCountdown((seconds) => (seconds === null ? null : seconds - 1)), 1000);
        return () => clearTimeout(timer);
    }, [countdown, logout]);

    useEffect(() => {
        if (status !== "resolved") {
            return;
        }

        let isCancelled = false;
        let isRefreshing = false;
        let timeoutId: ReturnType<typeof setTimeout>;

        function scheduleRefresh(delay: number) {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                void refreshPermissions();
            }, delay);
        }

        function scheduleFromLastValidation() {
            const lastValidatedAt = getPermissionsLastValidatedAt();
            const elapsed = lastValidatedAt === null ? PERMISSIONS_REFRESH_INTERVAL_MS : Date.now() - lastValidatedAt;
            scheduleRefresh(Math.max(0, PERMISSIONS_REFRESH_INTERVAL_MS - elapsed));
        }

        async function refreshPermissions() {
            if (isRefreshing) {
                return;
            }
            let token = expenseAdminTokenSource.getAccessToken();
            if (!token) {
                if (!isCancelled) {
                    scheduleRefresh(PERMISSIONS_REFRESH_INTERVAL_MS);
                }
                return;
            }

            isRefreshing = true;
            try {
                // Renew the session only when the access token is (near) expired. The
                // refresh token has its own lifetime: if it has also expired the auth
                // service returns 400 and no valid token remains — the session is over,
                // so sign out. Otherwise we keep the current token.
                if (isTokenExpired(token, TOKEN_REFRESH_BUFFER_SECONDS)) {
                    try {
                        token = await refreshTokenAndNotify();
                    } catch {
                        if (!hasValidSession()) {
                            endSession();
                            return;
                        }
                        console.warn("Background token refresh failed");
                        return;
                    }
                }

                // Re-check permissions with a valid token. A revoked account resolves to
                // an empty set and the no-access effect signs the user out; otherwise the
                // session simply continues.
                await expenseAdminPermissionResolver.resolve(token);
            } catch {
                console.warn("Background permission refresh failed");
            } finally {
                isRefreshing = false;
                if (!isCancelled && !loggingOutRef.current) {
                    scheduleRefresh(PERMISSIONS_REFRESH_INTERVAL_MS);
                }
            }
        }

        // Browsers freeze/throttle timers in hidden tabs, so a backgrounded tab
        // can miss its scheduled tick and never revoke access on its own. Re-arm
        // the timer the moment the tab is shown again: if the interval already
        // elapsed the refresh fires immediately (delay 0), logging the user out
        // without requiring any further interaction.
        function handleVisible() {
            if (isCancelled || document.visibilityState !== "visible") {
                return;
            }
            scheduleFromLastValidation();
        }

        document.addEventListener("visibilitychange", handleVisible);
        window.addEventListener("focus", handleVisible);

        scheduleFromLastValidation();

        return () => {
            isCancelled = true;
            clearTimeout(timeoutId);
            document.removeEventListener("visibilitychange", handleVisible);
            window.removeEventListener("focus", handleVisible);
        };
    }, [refreshTokenAndNotify, status, endSession]);

    const attemptRefresh = useCallback(async () => {
        if (attemptingRef.current) {
            return;
        }
        attemptingRef.current = true;
        setIsRetrying(true);
        try {
            // A new token makes RbacProvider re-resolve the allowed actions.
            await refreshTokenAndNotify();
        } catch {
            // Refresh failed. If no valid token remains the session is dead — log out
            // instead of parking the user on an error/forbidden screen.
            if (!hasValidSession()) {
                endSession();
            }
            // Otherwise transient; fall through to the final no-access / error handling.
        } finally {
            setIsRetrying(false);
            attemptingRef.current = false;
        }
    }, [refreshTokenAndNotify, endSession]);

    // Reset the retry budget once the user resolves to a healthy state.
    useEffect(() => {
        if (status === "resolved" && hasAccess) {
            setAttempts(0);
            loggingOutRef.current = false;
            shellReadyRef.current = true;
        }
    }, [status, hasAccess]);

    // Single automatic retry (token refresh + re-resolve) before giving up.
    useEffect(() => {
        const needsAttention = status === "error" || (status === "resolved" && !hasAccess);
        if (needsAttention && attempts < MAX_AUTO_ATTEMPTS && !isRetrying && !attemptingRef.current) {
            setAttempts((count) => count + 1);
            void attemptRefresh();
        }
    }, [status, hasAccess, attempts, isRetrying, attemptRefresh]);

    // After the retry, a resolved-but-empty result means the user truly has no
    // access: end the session and send them back to login with an explanation.
    useEffect(() => {
        if (status === "resolved" && !hasAccess && attempts >= MAX_AUTO_ATTEMPTS && !isRetrying) {
            endSession();
        }
    }, [status, hasAccess, attempts, isRetrying, endSession]);

    // A 403 from the API (stale client permissions) also ends the session in place.
    useEffect(() => {
        if (accessDenied) {
            endSession();
        }
    }, [accessDenied, endSession]);

    const handleRetry = useCallback(() => {
        // Re-arm the auto-retry effect, which drives a fresh refresh + re-resolve.
        setAttempts(0);
    }, []);

    // Shared with AppLayout so the access-denied notice + disabled sidebar render
    // in place (inside the shell) instead of a full-screen takeover. `isSigningOut`
    // covers the whole no-access flow (retry + countdown) so the content never
    // flashes the routed page / forbidden screen in between.
    const isSigningOut = countdown !== null || !hasAccess || accessDenied;
    const countdownValue = useMemo(() => ({ isSigningOut, secondsRemaining: countdown, message: NO_ACCESS_MESSAGE }), [isSigningOut, countdown]);

    // While the redirect countdown runs we keep the app shell mounted so the notice
    // shows in the content area. We also keep it mounted for any transition after the
    // shell first rendered (background re-resolve, revocation) so the screen doesn't
    // flash full-screen loaders — those only gate the very first load.
    if (countdown === null && !shellReadyRef.current) {
        if (status === "loading" || isRetrying) {
            return <LoadingState variant={LOADING_STATE_VARIANT.Block} label="Loading permissions" className="min-h-screen bg-gray-50" />;
        }

        if (status === "error") {
            if (attempts >= MAX_AUTO_ATTEMPTS) {
                return <ErrorPage message={NETWORK_ERROR_MESSAGE} onRetry={handleRetry} />;
            }
            return <LoadingState variant={LOADING_STATE_VARIANT.Block} label="Loading permissions" className="min-h-screen bg-gray-50" />;
        }

        if (status === "resolved" && !hasAccess) {
            // Retry pending or a silent (dead-session) sign-out is in flight.
            return <LoadingState variant={LOADING_STATE_VARIANT.Block} label="Signing out" className="min-h-screen bg-gray-50" />;
        }
    }

    return <AccessCountdownContext.Provider value={countdownValue}>{children}</AccessCountdownContext.Provider>;
}
